import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  MenuItem,
  TextField,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { apiRequest } from '../utils/api';

type GroupBy = 'year' | 'month' | 'day';

interface IncomeDataPoint {
  period: number;
  fundsToReceive: number;
  fundsReceived: number;
  growthRate: number | null;
}

interface IncomeResponse {
  groupBy: GroupBy;
  year: number | null;
  month: number | null;
  data: IncomeDataPoint[];
}

const MONTH_NAMES = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

const currentYear = new Date().getFullYear();
const yearOptions = Array.from({ length: 10 }, (_, i) => currentYear - i);
const monthOptions = MONTH_NAMES.map((name, i) => ({ value: i + 1, label: name }));

function formatCurrency(value: number) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value);
}

function getXLabel(groupBy: GroupBy, period: number): string {
  if (groupBy === 'month') return MONTH_NAMES[period - 1];
  return String(period);
}

function CustomTooltip({ active, payload, label, groupBy }: {
  active?: boolean;
  payload?: Array<{ name: string; value: number; color: string }>;
  label?: string | number;
  groupBy: GroupBy;
}) {
  if (!active || !payload || payload.length === 0) return null;

  const toReceive = payload.find((p) => p.name === 'Funds to Receive');
  const received  = payload.find((p) => p.name === 'Funds Received');
  const growth    = payload.find((p) => p.name === 'Growth %');

  return (
    <Box sx={{
      bgcolor: 'background.paper',
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 1,
      p: 1.5,
      boxShadow: 3,
      minWidth: 180,
    }}>
      <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
        {groupBy === 'month' ? MONTH_NAMES[Number(label) - 1] : label}
      </Typography>
      {toReceive && (
        <Typography variant="body2" color="primary.main">
          To Receive: {formatCurrency(toReceive.value)}
        </Typography>
      )}
      {received && (
        <Typography variant="body2" color="success.main">
          Received: {formatCurrency(received.value)}
        </Typography>
      )}
      {growth && growth.value !== null && (
        <Typography
          variant="caption"
          color={growth.value >= 0 ? 'success.main' : 'error.main'}
        >
          Growth: {growth.value >= 0 ? '+' : ''}{growth.value}%
        </Typography>
      )}
    </Box>
  );
}

export default function IncomeDashboard() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [groupBy, setGroupBy]           = useState<GroupBy>('month');
  const [selectedYear, setSelectedYear] = useState<number>(currentYear);
  const [selectedMonth, setSelectedMonth] = useState<number>(new Date().getMonth() + 1);

  const [data, setData]       = useState<IncomeDataPoint[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState<string | null>(null);

  const fetchIncome = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let url = `/reports/income?groupBy=${groupBy}`;
      if (groupBy === 'month') url += `&year=${selectedYear}`;
      if (groupBy === 'day')   url += `&year=${selectedYear}&month=${selectedMonth}`;

      const res  = await apiRequest(url, { method: 'GET' });
      const body = res.data as IncomeResponse;
      setData(body.data);
    } catch (err) {
      console.error(err);
      setError('Failed to load income data.');
    } finally {
      setLoading(false);
    }
  }, [groupBy, selectedYear, selectedMonth]);

  useEffect(() => { fetchIncome(); }, [fetchIncome]);

  // Summary stats
  const totalToReceive = data.reduce((s, d) => s + d.fundsToReceive, 0);
  const totalReceived  = data.reduce((s, d) => s + d.fundsReceived,  0);
  const filtered       = data.filter((d) => d.fundsReceived > 0);
  const lastPoint      = filtered[filtered.length - 1];
  const lastGrowth     = lastPoint?.growthRate ?? null;

  // Chart data with readable x-axis labels
  const chartData = data.map((d) => ({
    ...d,
    xLabel: getXLabel(groupBy, d.period),
  }));

  const colorToReceive = theme.palette.primary.main;
  const colorReceived  = theme.palette.success.main;
  const colorGrowth    = theme.palette.warning.main;

  const periodLabel =
    groupBy === 'year'  ? 'by Year' :
    groupBy === 'month' ? `by Month (${selectedYear})` :
                          `by Day (${MONTH_NAMES[selectedMonth - 1]} ${selectedYear})`;

  return (
    <Box>
      {/* Page header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="overline" color="text.secondary">Reports</Typography>
        <Typography variant="h4" component="h1" fontWeight={600}>Income</Typography>
      </Box>

      {/* Controls */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 3 }}>
        <ToggleButtonGroup
          value={groupBy}
          exclusive
          size="small"
          onChange={(_, val) => { if (val) setGroupBy(val as GroupBy); }}
        >
          <ToggleButton value="year">Year</ToggleButton>
          <ToggleButton value="month">Month</ToggleButton>
          <ToggleButton value="day">Day</ToggleButton>
        </ToggleButtonGroup>

        {(groupBy === 'month' || groupBy === 'day') && (
          <TextField
            select size="small" label="Year"
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
            sx={{ minWidth: 100 }}
          >
            {yearOptions.map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
          </TextField>
        )}

        {groupBy === 'day' && (
          <TextField
            select size="small" label="Month"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(Number(e.target.value))}
            sx={{ minWidth: 110 }}
          >
            {monthOptions.map((m) => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
          </TextField>
        )}
      </Box>

      {/* Summary cards */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(3, 1fr)',
        gap: 2,
        mb: 3,
      }}>
        {/* Funds to receive */}
        <Card variant="outlined">
          <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <AttachMoneyIcon sx={{ color: colorToReceive }} fontSize="large" />
            <Box>
              <Typography variant="caption" color="text.secondary">Funds to Receive</Typography>
              <Typography variant="h6" fontWeight={700}>{formatCurrency(totalToReceive)}</Typography>
            </Box>
          </CardContent>
        </Card>

        {/* Funds received */}
        <Card variant="outlined">
          <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <AccountBalanceWalletIcon sx={{ color: colorReceived }} fontSize="large" />
            <Box>
              <Typography variant="caption" color="text.secondary">Funds Received</Typography>
              <Typography variant="h6" fontWeight={700}>{formatCurrency(totalReceived)}</Typography>
            </Box>
          </CardContent>
        </Card>

        {/* Growth rate */}
        <Card variant="outlined" sx={{ gridColumn: isMobile ? '1 / -1' : undefined }}>
          <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {lastGrowth === null ? (
              <TrendingFlatIcon color="action" fontSize="large" />
            ) : lastGrowth >= 0 ? (
              <TrendingUpIcon color="success" fontSize="large" />
            ) : (
              <TrendingDownIcon color="error" fontSize="large" />
            )}
            <Box>
              <Typography variant="caption" color="text.secondary">Growth Rate (Received)</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h6" fontWeight={700}>
                  {lastGrowth === null ? '—' : `${lastGrowth >= 0 ? '+' : ''}${lastGrowth}%`}
                </Typography>
                {lastGrowth !== null && (
                  <Chip
                    size="small"
                    label="vs prev"
                    color={lastGrowth >= 0 ? 'success' : 'error'}
                    variant="outlined"
                  />
                )}
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Chart */}
      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} mb={2}>
            Income {periodLabel}
          </Typography>

          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          )}

          {error && !loading && <Alert severity="error">{error}</Alert>}

          {!loading && !error && (
            <ResponsiveContainer width="100%" height={380}>
              <ComposedChart data={chartData} margin={{ top: 8, right: 32, left: 8, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                <XAxis
                  dataKey="xLabel"
                  tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
                />
                {/* Left Y-axis: dollar amounts */}
                <YAxis
                  yAxisId="dollars"
                  tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`}
                  tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
                  width={60}
                />
                {/* Right Y-axis: growth % */}
                <YAxis
                  yAxisId="growth"
                  orientation="right"
                  tickFormatter={(v) => `${v}%`}
                  tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
                  width={48}
                />
                <Tooltip content={<CustomTooltip groupBy={groupBy} />} />
                <Legend />

                {/* Funds to Receive bar */}
                <Bar
                  yAxisId="dollars"
                  dataKey="fundsToReceive"
                  name="Funds to Receive"
                  fill={colorToReceive}
                  radius={[4, 4, 0, 0]}
                  maxBarSize={40}
                />

                {/* Funds Received bar */}
                <Bar
                  yAxisId="dollars"
                  dataKey="fundsReceived"
                  name="Funds Received"
                  fill={colorReceived}
                  radius={[4, 4, 0, 0]}
                  maxBarSize={40}
                />

                {/* Growth rate line */}
                <Line
                  yAxisId="growth"
                  type="monotone"
                  dataKey="growthRate"
                  name="Growth %"
                  stroke={colorGrowth}
                  strokeWidth={2}
                  dot={{ r: 4, fill: colorGrowth }}
                  activeDot={{ r: 6 }}
                  connectNulls={false}
                />
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
